/**
 * useMediaQuery and friends.
 *
 * Used by: the Admin shell (sidebar on desktop, drawer + BottomNav on phones),
 * the POS shell (which locks to landscape tablet layouts) and Table's
 * responsive column hiding when a screen needs it in JS rather than CSS.
 *
 * Prefer a Tailwind breakpoint class when the difference is purely visual —
 * this hook is for when the COMPONENT TREE differs, e.g. rendering a Drawer
 * instead of a Sidebar. Rendering both and hiding one wastes a mount.
 */
import { useEffect, useState } from 'react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent): void => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Matches Tailwind's `sm` breakpoint (640px). */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 639px)');
}

/** Between `sm` and `lg` — the POS tablet range. */
export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 640px) and (max-width: 1023px)');
}

/** OS-level dark preference, used to seed the theme before the user picks one. */
export function usePrefersDark(): boolean {
  return useMediaQuery('(prefers-color-scheme: dark)');
}
